import type {
  ActivityPageType,
  ActivityType,
  AssessmentType,
  BreakoutGroupType,
  CourseGroupingType,
  CourseType,
  GroupSubmissionType,
  IndividualSubmissionType,
  OrgUserType,
  OutcomeAssessmentType,
  OutcomeGroupType,
  OutcomeSubgroupType,
  OutcomeType,
  PageGroupType,
  PageGroupUserType,
  PageOutcomeType,
  PageType,
  SectionOutcomeType,
  SectionType,
  SectionUserType,
  SubmissionType,
  TermType,
  TopicType,
  UserInformationType,
  UserType,
} from "components/server-types";
import type { WithRequired } from "utils/type-utils";

export enum SectionUserActivityStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  OFFLINE = "offline",
}

export type EnabledSectionUser = SectionUserType & { is_disabled: false };

export type EnabledOrgUser = OrgUserType & { is_disabled: false };

export type UserWithSectionActivity = UserType & {
  section_user_id: string;
  sectionActivityStatus: SectionUserActivityStatus;
};

// NOTE: this is used in the SectionUsersSidebarPanel where we need to sort a list of users by
// section activity status and by specific fields on the ClassUser model.
export type EnhancedUserWithSectionActivity = UserType & {
  sectionActivityStatus: SectionUserActivityStatus;
  hand_raised_at: string | null;
  is_in_class_session: boolean;
  isFollowing: boolean;
};

export type OrderedSectionType = SectionType & { order: number };

export type SectionWithAssessmentInfoType = SectionType & {
  course: CourseType;
  pages: PageWithAssessmentInfoType[];
};

export type ObjectWithSections<T extends TermType | CourseType = TermType> = T & {
  sections: OrderedSectionType[];
};

export type CourseGroupingWithCoursesType = CourseGroupingType & {
  courses: CourseType[];
};

export type CourseWithGroupingType = CourseType & {
  groupingTitle: string | null;
};

export type ActivityWithPagesType = ActivityType & { pages: ActivityPageType[] };
export type TopicWithPagesType = WithRequired<
  TopicType,
  "released_page_count" | "unreleased_page_count"
> & { pages: PageType[] };

export type OutcomeWithScoreType = OutcomeType & {
  averageScore?: number;
};

export type NestedOutcomeSubgroupType = OutcomeSubgroupType & {
  outcomes: OutcomeWithScoreType[];
  averageOutcomeScore?: number;
};

export type NestedOutcomeGroupType = OutcomeGroupType & {
  outcomes: OutcomeWithScoreType[];
  outcomeSubgroups: NestedOutcomeSubgroupType[];
  outcomeCount: number;
  averageOutcomeScore?: number;
};

export type NestedSectionOutcomeType = SectionOutcomeType & {
  outcome: OutcomeType;
};

export type NestedPageOutcomeType = PageOutcomeType & {
  section_outcome: SectionOutcomeType;
  outcome: OutcomeType;
};

export type ExtendedPageGroupUserType = PageGroupUserType &
  UserInformationType & {
    user_id: string;
  };

export type NestedPageGroupType = PageGroupType & {
  page_group_users: ExtendedPageGroupUserType[];
};

export type PageWithDueDateType = PageType & { due_at: string };

export type FullIndividualSubmissionType = IndividualSubmissionType & {
  section_user: SectionUserType;
  assessment?: AssessmentType;
  outcome_assessments?: OutcomeAssessmentType[];
};

export type FullGroupSubmissionType = GroupSubmissionType & {
  assessment?: AssessmentType;
  outcome_assessments?: OutcomeAssessmentType[];
};

export type NestedAssessmentType = AssessmentType & {
  submission: SubmissionType;
  outcome_assessments: OutcomeAssessmentType[];
};

export type PageWithAssessmentsType = PageType & {
  topic: TopicType | null;
  assessments: NestedAssessmentType[];
  nonOfficialAssessments: NestedAssessmentType[];
};

export type PagesWithOutcomeAssessmentType = PageType & {
  assessment: NestedAssessmentType;
  outcomeAssessment: OutcomeAssessmentType;
  nonOfficialAssessments: NestedAssessmentType[];
};

export type PagesWithOutcomeAssessmentAndSectionType = PagesWithOutcomeAssessmentType & {
  section_title?: string;
};

export type PageWithTopicIdType = PageType & { topic_id: string | null };

export type PageWithAssessmentInfoType = PageType & {
  activity: ActivityType | null;
  topic: TopicType | null;
  page_outcomes: PageOutcomeType[];
  assessible_submissions_count: number;
  assessed_submissions_count: number;
  average_points: number | undefined;
};

export type NumberedBreakoutGroupType = BreakoutGroupType & {
  number: number;
};
